import React, { useState } from "react";
import PcoGroupItem from "./PcoGroupItem";
import JoltGroupItem from "./JoltGroupItem";
import { motion, AnimatePresence } from "framer-motion";
import useEnv from "../hooks/useEnv";

const ActivateDialog = ({ selectedGroups, onClose, onConfirm }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div 
        className="dashboard-element-bg rounded-lg p-6 max-w-2xl w-full mx-4 overflow-auto" 
        style={{ maxHeight: '80vh' }}
      >
        <h2 className="text-2xl font-bold mb-4">Activate Groups</h2>
        <p className="mb-4">
          An email will be sent to the group leaders informing them that their group is ready to be used on JoltChat.
          Once a leader activates the group via the email, you will see the group appear in your Active JoltChat Groups
        </p>
        <ul className="mb-6">
          {selectedGroups.map((group, index) => (
            <li key={index} className="flex items-center mb-2">
              <img
                src={group.grpAvatar}
                alt={group.title}
                className="w-8 h-8 rounded mr-2"
              />
              <span>{group.title}</span>
            </li>
          ))}
        </ul>
        <div className="flex justify-end space-x-4">
          <button 
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-gray-600 hover:bg-gray-700"
          >
            Cancel
          </button>
          <button 
            onClick={onConfirm}
            className="px-4 py-2 rounded-lg primary-gradient"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

const JoltChatGroups = ({ organization, groups }) => {
  return (
    <div className="dashboard-element-bg rounded-xl p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
      <h1 className="text-3xl font-bold mb-3">Manage Groups</h1>
      <div className="mt-6">
        <h2 className="text-2xl font-semibold mb-2">Active JoltChat Groups</h2>
        {groups.length > 0 ? (
          <ul>
            {groups.map((group, index) => (
              <AnimatePresence>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ 
                    duration: 0.5, 
                    delay: index * 0.1 
                  }}
                >
                  <JoltGroupItem key={index} group={group} />
                </motion.div>
              </AnimatePresence>
            ))}
          </ul>
        ) : (
          <p>No active JoltChat groups. Create groups in the JoltChat app to get started.</p>
        )}
      </div>

      </motion.div>
    </div>
  );
}

const GroupsSection = ({ organization, groups = [] }) => {
  const envUrl = useEnv();
  const [activeToggles, setActiveToggles] = useState(0);
  const [toggleAll, setToggleAll] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState([]);
    
  if ( organization.orgType !== 'joltchat' ) {  
    const activeGroups = groups
      .filter(group => group.joltActivated)
      .sort((a, b) => a.title.localeCompare(b.title));
      
    const inactiveGroups = groups
      .filter(group => !group.joltActivated)
      .sort((a, b) => a.title.localeCompare(b.title));

    const handleToggle = (isChecked, group) => {
      setActiveToggles(prev => isChecked ? prev + 1 : prev - 1);
      setSelectedGroups(prev => 
        isChecked 
          ? [...prev, group]
          : prev.filter(g => g.title !== group.title)
      );
    };

    const handleToggleAll = () => {
      const newToggleState = !toggleAll;
      setToggleAll(newToggleState);
      setActiveToggles(newToggleState ? inactiveGroups.length : 0);
      setSelectedGroups(newToggleState ? inactiveGroups : []);
    };

    const handleActivateClick = () => {
      setShowDialog(true);
    };

    const handleActivation = async () => {
      setShowDialog(false);
      console.log(organization);
      
      try {
        const promises = selectedGroups.map(group => {
          console.log(group.pco_id);
          console.log(organization.organizationId);
          // return fetch('http://127.0.0.1:5001/joltchat/us-central1/groupEmailManager', { //dev
          return fetch(`${envUrl}/groupEmailManager`, { //prod
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ group_id: group.pco_id, org_id: organization.organizationId, flag: 'leaders' })
          });
        });

        await Promise.all(promises);
        console.log('Emails sent to leaders');
      } catch (error) {
        console.error('Error:', error);
        alert('Error sending emails to group leaders');
      }
    };

    return (
      <div className="dashboard-element-bg rounded-xl p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
        <h1 className="text-3xl font-bold mb-3">Manage Groups</h1>
        {activeGroups.length === 0 ? <p>Activate your JoltChat groups</p> : ""}

        <div className="mt-6">
          <h2 className="text-2xl font-semibold mb-2">Active JoltChat Groups</h2>
          {activeGroups.length > 0 ? (
            <ul>
              {activeGroups.map((group, index) => (
                <AnimatePresence>
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ 
                      duration: 0.5, 
                      delay: index * 0.1 
                    }}
                  >
                    <PcoGroupItem key={index} group={group} onToggle={handleToggle} />
                  </motion.div>
                </AnimatePresence>
              ))}
            </ul>
          ) : (
            <p>No active Jolt groups.</p>
          )}
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-2xl font-semibold">Inactive JoltChat Groups</h2>
            <label className="switch groups-toggle pr-4">
              <input 
                type="checkbox"
                className="groupchk" 
                checked={toggleAll}
                onChange={handleToggleAll}
                style={{ backgroundColor: '#1E1D2A' }}
              />
              <span className="slider round"></span>
            </label>
          </div>
          {inactiveGroups.length > 0 ? (
            <ul>
              {inactiveGroups.map((group, index) => (
                <AnimatePresence>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ 
                    duration: 0.5, 
                    delay: index * 0.1 
                  }}
                >
                  <PcoGroupItem key={index} group={group} onToggle={handleToggle} isChecked={toggleAll} />
                </motion.div>
              </AnimatePresence>
              ))}
            </ul>
          ) : (
            <p>No inactive JoltChat groups.</p>
          )}
        </div>

        {activeToggles > 0 && (
          <button 
            onClick={handleActivateClick}
            className="fixed bottom-12 left-1/2 transform -translate-x-1/2 primary-gradient text-white font-semibold text-2xl py-8 px-16 rounded-full shadow-lg animate-slide-up"
          >
            Activate
          </button>
        )}

        {showDialog && (
          <ActivateDialog 
            selectedGroups={selectedGroups} 
            onClose={() => setShowDialog(false)}
            onConfirm={handleActivation}           
          />
        )}
        </motion.div>
      </div>
    );
  } else {
    return <JoltChatGroups organization={organization} groups={groups} />;
  }
};

export default GroupsSection;